import type { ExistDict, TeamOut } from '@repo/api-codegen';
import { useApiListTeamsV2 } from '@repo/api-codegen/v2';
import { useMemo } from 'react';

/**
 * Hook to get all teams with their empty states.
 * This fetches all teams in one request, and each team includes empty_state.
 */
export const useTeamsList = (options: { enabled?: boolean } = {}) =>
	useApiListTeamsV2(
		{},
		{
			...options,
		}
	);

/**
 * Hook to get empty state for a specific team and section.
 * Uses the list teams endpoint which includes empty_state for each team.
 */
export function useTeamEmptyState(
	teamId: string,
	sectionId: keyof ExistDict,
	options: { enabled?: boolean } = {}
) {
	const {
		data: teamsData,
		isLoading,
		isError,
		...rest
	} = useTeamsList({
		enabled: options.enabled !== false,
	});

	const emptyState = useMemo(() => {
		if (!teamsData) {
			return { is_empty: false };
		}

		const team = teamsData.find((t: TeamOut) => t.id === teamId);

		if (!team?.empty_state) {
			return { is_empty: false };
		}

		return team.empty_state[sectionId] ?? { is_empty: false };
	}, [teamsData, teamId, sectionId]);

	return {
		...emptyState,
		isLoading,
		isError,
		...rest,
	};
}

/**
 * Hook to get all empty states for a specific team.
 * Uses the list teams endpoint which includes empty_state for each team.
 */
export const useTeamEmptyStates = (
	teamId: string,
	options: { enabled?: boolean } = {}
) => {
	const { data: teamsData, ...rest } = useTeamsList({
		enabled: options.enabled !== false,
	});

	const teamSettings = useMemo(() => {
		if (!teamsData) {
			return undefined;
		}

		const found = teamsData.find((t: TeamOut) => t.id === teamId);

		if (!found?.empty_state) {
			return undefined;
		}

		return {
			exist_dict: found.empty_state,
		};
	}, [teamsData, teamId]);

	return {
		...rest,
		data: teamSettings,
	};
};
